import { useState } from 'react';
import { Play } from 'lucide-react';
import { cn } from '@/lib/utils';

interface VideoShowcaseProps {
  section: any;
}

export default function VideoShowcase({ section }: VideoShowcaseProps) {
  const content = section.content ?? {};
  const { videoUrl, thumbnailUrl, title, description, ctaText, ctaLink } = content;
  const headingId = `video-showcase-heading-${section.id}`;
  const [isPlaying, setIsPlaying] = useState(false);

  const isYouTubeOrVimeo =
    videoUrl?.includes('youtube.com') ||
    videoUrl?.includes('youtu.be') ||
    videoUrl?.includes('vimeo.com');

  return (
    <section aria-labelledby={headingId} className="py-16 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Section header */}
        {section.title && (
          <div className="text-center mb-12">
            <h2
              id={headingId}
              className="font-display text-3xl font-bold text-sage-900 sm:text-4xl"
            >
              {section.title}
            </h2>
            {section.subtitle && (
              <p className="mt-2 text-sage-600 max-w-xl mx-auto">
                {section.subtitle}
              </p>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Video / Thumbnail */}
          <div className="relative rounded-xl overflow-hidden shadow-lg bg-sage-900 aspect-video">
            {isPlaying && videoUrl ? (
              isYouTubeOrVimeo ? (
                <iframe
                  src={videoUrl.replace('watch?v=', 'embed/') + '?autoplay=1'}
                  title={title || 'Video'}
                  className="absolute inset-0 w-full h-full"
                  allow="autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <video
                  src={videoUrl}
                  className="absolute inset-0 w-full h-full object-cover"
                  controls
                  autoPlay
                />
              )
            ) : (
              <button
                type="button"
                onClick={() => setIsPlaying(true)}
                className="absolute inset-0 w-full h-full group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600 focus-visible:ring-offset-2 rounded-xl"
                aria-label={`Play video: ${title || 'Video'}`}
              >
                {thumbnailUrl ? (
                  <img
                    src={thumbnailUrl}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-sage-800" />
                )}
                {/* Darkened overlay */}
                <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors" />
                {/* Play button */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/90 shadow-lg group-hover:scale-110 transition-transform">
                    <Play className="h-7 w-7 text-sage-900 ml-1" aria-hidden="true" />
                  </div>
                </div>
              </button>
            )}
          </div>

          {/* Text content */}
          <div>
            {title && (
              <h3 className="font-display text-2xl font-bold text-sage-900 sm:text-3xl">
                {title}
              </h3>
            )}
            {description && (
              <p className="mt-4 text-sage-600 leading-relaxed">
                {description}
              </p>
            )}
            {ctaText && ctaLink && (
              <a
                href={ctaLink}
                className={cn(
                  'mt-6 inline-flex items-center rounded-lg bg-cta px-6 py-2.5 text-sm font-semibold text-cta-fg',
                  'hover:bg-cta-deep transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600 focus-visible:ring-offset-2'
                )}
              >
                {ctaText}
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
